import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { HttpApiService } from "@/shared/services/httpApi.service";
import type { OrganizationStatus } from "@/shared/types/organization.entity";
import { createAccessLatch } from "@/shared/utils/accessLatch";

import {
  ORGANIZATION_SEARCH_DEBOUNCE_MS,
  ORGANIZATION_SEARCH_MIN_LENGTH,
  ORGANIZATIONS_PAGE_SIZE,
} from "../constants/organizationsAdmin.constants";
import { OrganizationsAdminService } from "../services/organizationsAdmin.service";
import type {
  AdminOrganization,
  OrganizationFeature,
  PayoutBatchesFilter,
} from "../types/organizationsAdmin.types";

const listAccessLatch = createAccessLatch();

export type FeatureToggleResult = "ok" | "failed";

export interface UseOrganizationsAdminListResult {
  search: string;
  setSearch: (search: string) => void;
  /** True while the typed term is too short to reach the server (it would be a 400). */
  searchTooShort: boolean;
  status: OrganizationStatus | null;
  setStatus: (status: OrganizationStatus | null) => void;
  payoutBatches: PayoutBatchesFilter;
  setPayoutBatches: (filter: PayoutBatchesFilter) => void;
  hasActiveFilters: boolean;
  clearFilters: () => void;
  page: number;
  setPage: (page: number) => void;
  rows: AdminOrganization[];
  total: number;
  isLoading: boolean;
  hasError: boolean;
  hasAccess: boolean;
  refresh: () => void;
  /** Ids whose switch is being written; their switch stays disabled until the answer lands. */
  pendingIds: ReadonlySet<string>;
  toggleFeature: (
    organization: AdminOrganization,
    feature: OrganizationFeature,
    enabled: boolean,
  ) => Promise<FeatureToggleResult>;
}

const toFilterValue = (filter: PayoutBatchesFilter): boolean | undefined =>
  filter === "all" ? undefined : filter === "enabled";

const ORGANIZATION_FEATURES: readonly OrganizationFeature[] = [
  "payout_batches_enabled",
  "breb_receiving_key_enabled",
  "otc_enabled",
  "card_deposits_enabled",
  "card_cop_top_ups_enabled",
  "breb_qr_payments_enabled",
  "card_wallet_enabled",
  "can_customize_card_design",
];

const switchOverrideKey = (organizationId: string, feature: OrganizationFeature): string =>
  `${organizationId}:${feature}`;

/**
 * The staff directory of client organizations: server-side search (name, email or id), status and
 * batch-payouts filters, and the per-organization feature switch. A switch flips optimistically and rolls
 * back if the write fails; a row whose new value no longer matches the filter stays until the next read, so
 * it does not vanish under the cursor.
 */
export function useOrganizationsAdminList(): UseOrganizationsAdminListResult {
  const { user } = useAuth();
  const deniedKey = user?.id ?? "anon";
  const hasAccess = !listAccessLatch.useDenied(deniedKey);

  const [search, setSearchState] = useState("");
  const [status, setStatusState] = useState<OrganizationStatus | null>(null);
  const [payoutBatches, setPayoutBatchesState] = useState<PayoutBatchesFilter>("all");
  const [page, setPage] = useState(1);
  const [reloadToken, setReloadToken] = useState(0);

  const debouncedSearch = useDebouncedValue(search.trim(), ORGANIZATION_SEARCH_DEBOUNCE_MS);
  const term = debouncedSearch.length >= ORGANIZATION_SEARCH_MIN_LENGTH ? debouncedSearch : "";
  const trimmed = search.trim();
  const searchTooShort = trimmed.length > 0 && trimmed.length < ORGANIZATION_SEARCH_MIN_LENGTH;

  const [rows, setRows] = useState<AdminOrganization[]>([]);
  const [total, setTotal] = useState(0);
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
  const requestSeq = useRef(0);
  // A read that started before a switch write was answered must not put the old value back on screen.
  const switchOverrides = useRef(new Map<string, { enabled: boolean; untilSeq: number }>());

  const queryKey = useMemo(
    () => [term, status ?? "*", payoutBatches, page, reloadToken].join("|"),
    [page, payoutBatches, reloadToken, status, term],
  );
  const isLoading = hasAccess && settledKey !== queryKey;

  const fetchPage = useCallback(async () => {
    if (listAccessLatch.isDenied(deniedKey)) return;
    const seq = ++requestSeq.current;
    try {
      const { data } = await OrganizationsAdminService.list({
        skip: (page - 1) * ORGANIZATIONS_PAGE_SIZE,
        limit: ORGANIZATIONS_PAGE_SIZE,
        search: term || undefined,
        status: status ?? undefined,
        payoutBatchesEnabled: toFilterValue(payoutBatches),
      });
      if (seq !== requestSeq.current) return;
      setRows(
        (data.data ?? []).map((row) => {
          let next = row;
          for (const feature of ORGANIZATION_FEATURES) {
            const overrideKey = switchOverrideKey(row.id, feature);
            const override = switchOverrides.current.get(overrideKey);
            if (!override) continue;
            if (seq > override.untilSeq) {
              switchOverrides.current.delete(overrideKey);
              continue;
            }
            next = { ...next, [feature]: override.enabled };
          }
          return next;
        }),
      );
      setTotal(data.total ?? 0);
      setHasError(false);
      setSettledKey(queryKey);
    } catch (err: unknown) {
      if (seq !== requestSeq.current) return;
      if (HttpApiService.isAccessDeniedError(err)) {
        listAccessLatch.latch(deniedKey);
        return;
      }
      setRows([]);
      setTotal(0);
      setHasError(true);
      setSettledKey(queryKey);
    }
  }, [deniedKey, page, payoutBatches, queryKey, status, term]);

  const fetchPageRef = useRef(fetchPage);
  useEffect(() => {
    fetchPageRef.current = fetchPage;
  });

  useEffect(() => {
    void fetchPageRef.current();
  }, [queryKey, deniedKey]);

  const setSearch = useCallback((next: string) => {
    setSearchState(next);
    setPage(1);
  }, []);
  const setStatus = useCallback((next: OrganizationStatus | null) => {
    setStatusState(next);
    setPage(1);
  }, []);
  const setPayoutBatches = useCallback((next: PayoutBatchesFilter) => {
    setPayoutBatchesState(next);
    setPage(1);
  }, []);
  const clearFilters = useCallback(() => {
    setSearchState("");
    setStatusState(null);
    setPayoutBatchesState("all");
    setPage(1);
  }, []);
  const refresh = useCallback(() => setReloadToken((token) => token + 1), []);

  const patchRow = useCallback((id: string, feature: OrganizationFeature, enabled: boolean) => {
    setRows((current) => current.map((row) => (row.id === id ? { ...row, [feature]: enabled } : row)));
  }, []);

  const toggleFeature = useCallback(
    async (
      organization: AdminOrganization,
      feature: OrganizationFeature,
      enabled: boolean,
    ): Promise<FeatureToggleResult> => {
      const previous = organization[feature];
      setPendingIds((current) => new Set(current).add(organization.id));
      const overrideKey = switchOverrideKey(organization.id, feature);
      switchOverrides.current.set(overrideKey, { enabled, untilSeq: Number.POSITIVE_INFINITY });
      patchRow(organization.id, feature, enabled);
      try {
        const { data } = await OrganizationsAdminService.updateFeatures(organization.id, {
          [feature]: enabled,
        });
        switchOverrides.current.set(overrideKey, {
          enabled: data[feature],
          untilSeq: requestSeq.current,
        });
        patchRow(organization.id, feature, data[feature]);
        return "ok";
      } catch {
        switchOverrides.current.delete(overrideKey);
        patchRow(organization.id, feature, previous);
        return "failed";
      } finally {
        setPendingIds((current) => {
          const next = new Set(current);
          next.delete(organization.id);
          return next;
        });
      }
    },
    [patchRow],
  );

  return {
    search,
    setSearch,
    searchTooShort,
    status,
    setStatus,
    payoutBatches,
    setPayoutBatches,
    hasActiveFilters: trimmed.length > 0 || status !== null || payoutBatches !== "all",
    clearFilters,
    page,
    setPage,
    rows,
    total,
    isLoading,
    hasError,
    hasAccess,
    refresh,
    pendingIds,
    toggleFeature,
  };
}
